import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import { getDeviceId } from '@/lib/device';
import { establishSession, endSession } from '@/lib/session';

const API_BASE_URL = '/api/proxy';

/** Thrown by fetchApi so callers can distinguish rate-limiting from other failures. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Machine-readable error code from the API envelope, when it sends one.
     *  Prefer branching on this over the human-readable message — the message is
     *  display copy and changing its wording must not silently break callers. */
    public readonly code?: string,
    /** Full parsed error response body, for endpoints whose error envelope carries
     *  extra fields beyond message/code (e.g. EVENT_RESULTS_LOCKED's unlocksAt,
     *  eventTitle, eventSlug). Most callers only need `code`/`message` — this is
     *  an escape hatch, not a replacement. */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Requests authenticate with the httpOnly `aks_session` cookie, which the browser attaches
 * automatically (same-origin) and the proxy forwards to the API — see
 * docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §4.2. No Bearer token is
 * attached here.
 *
 * This deliberately does NOT await Firebase. The previous implementation awaited
 * `authStateReady()` + `getIdToken()` before every call, which serialised each request behind
 * the SDK restoring auth state — measured at ~980ms on a cold load, paid before the first
 * app-data request could even start.
 */
function request(endpoint: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Id': getDeviceId(),
  };

  return fetch(`${API_BASE_URL}${endpoint}`, {
    headers,
    credentials: 'same-origin',
    ...options,
  });
}

/**
 * Shared so a burst of parallel 401s (a whole page's fetches racing an expired cookie) triggers
 * exactly one mint rather than one per request.
 */
let healInFlight: Promise<boolean> | null = null;

/**
 * Re-mints `aks_session` from Firebase after a 401.
 *
 * Without this, dropping the pre-request Firebase await would turn an expired cookie into a hard
 * failure: the old code accidentally guaranteed a fresh credential on every call because it
 * always waited for a token. The mount-time bootstrap heal in AuthContext (design doc §5.2)
 * only covers page load, not a cookie expiring mid-session.
 *
 * Returns false when there is no Firebase user — genuinely signed out, so a retry would just
 * 401 again.
 */
function healSession(): Promise<boolean> {
  if (healInFlight) return healInFlight;

  healInFlight = (async () => {
    try {
      await auth.authStateReady();
      const user = auth.currentUser;
      if (!user) return false;
      // force=true: the cookie is stale, so a cached ID token may be too.
      await establishSession(await user.getIdToken(true));
      return true;
    } catch {
      return false;
    }
  })();

  void healInFlight.finally(() => { healInFlight = null; });
  return healInFlight;
}

/** Paths where a terminal 401 must NOT bounce to /login — the user is already in an
 * auth flow, and redirecting would loop. Matched by prefix at both call sites (forceReLogin
 * here and the SessionMintError branch in AuthContext). /auth/action and /auth/verify consume
 * one-shot codes and hold a half-filled reset form, so a bounce there is destructive. */
export const AUTH_FLOW_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/verify-email',
  '/auth/action',
  '/auth/verify',
];

let reLoginInFlight = false;

/**
 * Terminal-401 handler: the session cookie is dead and could not be re-minted (no Firebase
 * user, mint endpoint rejected, or the freshly minted session was still refused). Tear the
 * half-dead session down fully — clear the cookie + Firebase signOut — then send the user to
 * /login with a redirect back to where they were. Without the teardown the login page sees
 * a lingering Firebase user and bounces straight back into the same 401 loop.
 *
 * revoke:false is deliberate. This used to call endSession() with its default revoke, which
 * runs revokeRefreshTokens() — invalidating every session cookie and ID token for the account
 * on EVERY device. That made a single unauthenticated request on one phone log the user out
 * of every other device, and (because the API used to mint without checkRevoked) let the very
 * next mint issue a cookie whose auth_time already predated the revocation, so it was rejected
 * forever: 401 -> revoke -> mint a dead cookie -> 401. A client that cannot use its own cookie
 * gets to discard its own cookie, nothing more.
 *
 * One-shot: a page's burst of parallel failing fetches triggers a single redirect.
 */
function forceReLogin(): void {
  if (reLoginInFlight) return;
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (AUTH_FLOW_PATHS.some((p) => pathname.startsWith(p))) return;
  reLoginInFlight = true;

  void (async () => {
    try { await endSession({ revoke: false }); } catch { /* cookie may already be gone */ }
    try { await signOut(auth); } catch { /* WebKit can tear down IndexedDB mid-session */ }
    window.location.href = `/login?redirect=${encodeURIComponent(pathname + search)}`;
  })();
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  let response = await request(endpoint, options);

  // One retry, only after a successful re-mint. A second 401 falls through and throws.
  if (response.status === 401 && await healSession()) {
    response = await request(endpoint, options);
  }

  if (!response.ok) {
    // Still 401 after the heal attempt (or heal refused): the session is unrecoverable
    // client-side. Redirect to login instead of leaving every page to render its own
    // raw "Unauthorized" while the chrome still shows a signed-in user.
    if (response.status === 401) forceReLogin();
    // Falsy .error on the fallback so the `HTTP ${status}` branch can actually fire.
    const error = await response.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error.code, error);
  }

  return response.json();
}
